import { useState, useCallback, useEffect } from 'react'

import { usePannellumViewer } from '../../../../hooks/usePannellum'

/** Tag id is encoded on hotspots as `pnlm-id_${tagId}` (see usePannellum addHotSpot). */
export function parseHotspotTagIdFromTarget(
  target: EventTarget | null,
): number | null {
  if (!(target instanceof Element)) return null
  const hotspotEl = target.closest('.pnlm-hotspot')
  if (!hotspotEl || hotspotEl.classList.contains('pnlm-TOUR')) return null
  const idClass = Array.from(hotspotEl.classList).find(c =>
    c.startsWith('pnlm-id_'),
  )
  if (!idClass) return null
  const raw = idClass.slice('pnlm-id_'.length)
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export const useContextMenu = (viewerId: string) => {
  const [xPos, setXPos] = useState('0px')
  const [yPos, setYPos] = useState('0px')
  const [pitch, setPitch] = useState<number | null>(null)
  const [yaw, setYaw] = useState<number | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [isHotspot, setIsHotspot] = useState(false)
  const [hotspotTagId, setHotspotTagId] = useState<number | null>(null)
  const { viewer, cleanTemporaryTags } = usePannellumViewer(viewerId)

  const handleContextMenu = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
    (e: any) => {
      e.preventDefault()
      setXPos(`${e.pageX}px`)
      setYPos(`${e.pageY}px`)
      setShowMenu(true)
      const clickedOnTmpTag = e.target.classList.contains('pnlm-tmp')
      // Pannellum does not invoke contextMenuHandler; resolve id from the DOM.
      const tagId = parseHotspotTagIdFromTarget(e.target)

      if (viewer) {
        const newCoordinates = viewer.mouseEventToCoords(e)
        const newPitch = newCoordinates[0]
        const newYaw = newCoordinates[1]
        setPitch(newPitch)
        setYaw(newYaw)
      }

      if (tagId != null && !clickedOnTmpTag) {
        setIsHotspot(true)
        setHotspotTagId(tagId)
      } else {
        setIsHotspot(false)
        setHotspotTagId(null)

        if (clickedOnTmpTag) {
          cleanTemporaryTags()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: cleanTemporaryTags intentionally omitted to prevent infinite loops
    [setXPos, setYPos, viewer],
  )

  const handleClick = useCallback(() => {
    showMenu && setShowMenu(false)
  }, [showMenu])

  useEffect(() => {
    const masterIV = document.getElementById('image-viewer-master')
    if (masterIV) {
      masterIV.addEventListener('click', handleClick)
      masterIV.addEventListener('contextmenu', handleContextMenu)
    }
    return () => {
      if (masterIV) {
        masterIV.removeEventListener('click', handleClick)
        masterIV.removeEventListener('contextmenu', handleContextMenu)
      }
    }
  })

  return { xPos, yPos, showMenu, isHotspot, hotspotTagId, pitch, yaw }
}
